import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from 'primereact/icons/plus';
import { LiliButton } from '@lili/lili-frontend-common';
import { nameTemplate } from './cell-templates/item-name-template';
import { descriptionTemplate } from './cell-templates/item-description-template';
import { IProduct } from '../../../InvoiceCreation/types/types';
import { priceTemplate } from './cell-templates/item-price-template';
import { TEXTS } from '../constants';
import Heading from '../../../../../generic-components/Heading';
import { ReactComponent as InvoiceIcon } from '../../../../../assets/icons/svg/stamp-icons/ic-stamp-invoice.svg';
import { featureAccess, LimitedFeatureType } from '../../../../../services/appAccess.service';
import { FeatureAccess } from '../../../../AccountSettings/components/rolesAndPermissions/PermissionsList';

interface IProductsTableProps {
  products: IProduct[];
  onEditClick: (product: IProduct) => void;
  onDeleteClick: (product: IProduct) => void;
  onAddNewClick: () => void;
  showEmptyState: boolean;
}

export const ProductsTable = ({
  products,
  onEditClick,
  onDeleteClick,
  onAddNewClick,
  showEmptyState,
}: IProductsTableProps) => {
  const { t } = useTranslation();
  const { DESCRIPTION_COLUMN, PRICE_COLUMN, EMPTY_STATE } = TEXTS.SERVICES_AND_PRODUCTS_HUB.PRODUCTS_TABLE;
  const headerClassName = 'py-[10px] px-0 font-metricRSemibold text-[14px] text-bw07 text-left';
  const columnClassName = 'p-0';
  const [lastMenuRef, setLastMenuRef] = useState<any | null>(null);
  const invoicesPermission = featureAccess(null, LimitedFeatureType.Invoices);

  return (
    <div className='z-10 max-h-screen overflow-y-scroll border-[1px] rounded-[8px] border-bw03'>
      <DataTable
        id='data-table-prime-react-products'
        value={products}
        selectionMode='single'
        onSelectionChange={() => {}}
        tableStyle={{ minWidth: '50rem' }}
        scrollable
        scrollHeight='70vh'
        className='p-0 m-0'
        emptyMessage={
          showEmptyState && (
            <div className='flex flex-col w-full items-center pt-[157px]'>
              <InvoiceIcon />
              <Heading
                title={t(EMPTY_STATE.TITLE)}
                className='mb-[24px] font-metricR text-[18px] text-bw08  mt-[18px]'
              />
              <LiliButton
                id='planPickerTab'
                title={t(EMPTY_STATE.ADD_BUTTON)}
                className='text-[14px] mb-[121px] w-[190px] h-[40px]'
                onClick={onAddNewClick}
                icon={<PlusIcon className='mr-[10px]' />}
              />
            </div>
          )
        }
      >
        <Column
          headerClassName={`${headerClassName} pl-[26px]`}
          className={`${columnClassName} w-[120px] max-w-[140px] pr-[24px]`}
          field='name'
          header={t(TEXTS.SERVICES_AND_PRODUCTS_HUB.PRODUCTS_TABLE.NAME_COLUMN)}
          sortable
          sortField='name'
          body={nameTemplate}
        />
        <Column
          headerClassName={headerClassName}
          className={`${columnClassName} w-[200px] pr-[24px]`}
          field='description'
          header={t(DESCRIPTION_COLUMN)}
          body={descriptionTemplate}
        />
        <Column
          headerClassName={headerClassName}
          className={`${columnClassName} w-[100px]`}
          field='price'
          header={`${t(PRICE_COLUMN)} ($)`}
          sortable
          sortField='amount'
          body={(product) =>
            priceTemplate(
              product,
              (menuRef, event) => {
                lastMenuRef?.hide(event);
                menuRef.toggle(event);
                setLastMenuRef(menuRef);
              },
              () => {
                onEditClick(product);
              },
              () => {
                onDeleteClick(product);
              },
              invoicesPermission === FeatureAccess.READ_WRITE,
            )}
        />
      </DataTable>
    </div>
  );
};
